import {
  Badge,
  DataTable,
  type DataTableColumn,
  EmptyTableState,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { useParams } from 'react-router-dom';

interface ActivityItem {
  id: string;
  eventType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

const EVENT_TYPE_LABELS: Record<string, { label: string; variant: string }> = {
  tour_completed: { label: 'Tour Completed', variant: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  attraction_visited: { label: 'Attraction Visited', variant: 'bg-blue-50 text-blue-700 border-blue-200' },
  offer_redeemed: { label: 'Offer Redeemed', variant: 'bg-purple-50 text-purple-700 border-purple-200' },
  photo_submitted: { label: 'Photo Submitted', variant: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function getEventLabel(eventType: string): { label: string; variant: string } {
  return EVENT_TYPE_LABELS[eventType] || {
    label: eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    variant: 'bg-gray-50 text-gray-700 border-gray-200',
  };
}

const columns: DataTableColumn<ActivityItem>[] = [
  {
    key: 'eventType',
    label: 'Event Type',
    format: (value: string) => {
      const { label, variant } = getEventLabel(value);
      return <Badge className={variant}>{label}</Badge>;
    },
  },
  {
    key: 'entityId',
    label: 'Entity ID',
    format: (value: string | null) =>
      value ? (
        <span className="font-mono text-sm">{value}</span>
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    key: 'createdAt',
    label: 'Date',
    type: 'datetime',
  },
];

export function HotelUserActivityTab() {
  const { hotelId, userId } = useParams<{ hotelId: string; userId: string }>();
  const apiClient = useApiClient();

  const { data, isLoading } = useQuery<{ success: boolean; data: { activities: ActivityItem[] } }>({
    queryKey: ['user-activities', hotelId, userId],
    queryFn: () => apiClient.get(`/hotels/${hotelId}/scanners/${userId}/activities`),
    enabled: !!hotelId && !!userId,
  });

  const activities = data?.data?.activities || [];

  return (
    <DataTable<ActivityItem>
      columns={columns}
      data={activities}
      loading={isLoading}
      emptyState={
        <EmptyTableState
          icon={<Activity className="h-8 w-8" />}
          title="No activity yet"
          description="Activity events like tour completions, attraction visits, and offer redemptions will appear here."
        />
      }
      skeletonRows={5}
    />
  );
}
